import React, { useEffect, useRef, useCallback } from 'react';

const GlowBackground = ({ 
  splitMode = false, 
  leftActive = false, 
  rightActive = false, 
  merged = false,
  dropFromTop = false, // Triggers drop glow going DOWN (text disappearing)
  riseFromMiddle = false // Triggers glow going UP (text appearing)
}) => {
  const canvasRef = useRef(null);
  const mousePos = useRef({ x: 0.5, y: 0.5 });
  const currentPos = useRef({ x: 0.5, y: 0.5 });
  const leftPos = useRef({ x: 0.25, y: 0.5 });
  const rightPos = useRef({ x: 0.75, y: 0.5 });
  const animationRef = useRef(null);
  const isMobile = useRef(false);
  const timeRef = useRef(0);
  const mergeProgress = useRef(0);
  
  // Drop glow state (goes DOWN when text disappears)
  const dropGlowActive = useRef(false);
  const dropGlowY = useRef(-0.2);
  const dropGlowOpacity = useRef(0);
  
  // Rise glow state (goes UP when text appears)
  const riseGlowActive = useRef(false);
  const riseGlowY = useRef(0.5);
  const riseGlowOpacity = useRef(0);

  // Lerp function for smooth movement
  const lerp = (start, end, factor) => {
    return start + (end - start) * factor;
  };

  // Check if mobile on mount
  useEffect(() => {
    isMobile.current = window.matchMedia('(max-width: 768px)').matches || 
                       'ontouchstart' in window;
  }, []);

  // Handle drop from top animation (text disappearing)
  useEffect(() => {
    if (dropFromTop) {
      dropGlowActive.current = true;
      dropGlowY.current = -0.15;
      dropGlowOpacity.current = 0.35;
    }
  }, [dropFromTop]);

  // Handle rise from middle animation (text appearing)
  useEffect(() => {
    if (riseFromMiddle) {
      riseGlowActive.current = true;
      riseGlowY.current = 0.45;
      riseGlowOpacity.current = 0.35;
    }
  }, [riseFromMiddle]);

  // Mouse move handler
  const handleMouseMove = useCallback((e) => {
    if (!isMobile.current && !splitMode) {
      mousePos.current = {
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight
      };
    }
  }, [splitMode]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  // Main animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resize();
    window.addEventListener('resize', resize);

    const animate = () => {
      timeRef.current += 0.016;
      const time = timeRef.current;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Cursor glow pulse - 0.25 base
      const basePulse = 0.25 + (Math.sin(time * 0.8) * 0.04);
      const flickerPulse = Math.sin(time * 2.3) * 0.012 + Math.sin(time * 3.7) * 0.008;
      const pulseOpacity = basePulse + flickerPulse;
      
      if (splitMode) {
        // Update merge progress
        if (merged) {
          mergeProgress.current = lerp(mergeProgress.current, 1, 0.03);
        } else {
          mergeProgress.current = lerp(mergeProgress.current, 0, 0.05);
        }
        
        const mp = mergeProgress.current;
        
        const leftTargetX = lerp(0.25, 0.5, mp);
        const rightTargetX = lerp(0.75, 0.5, mp);
        
        const driftX = Math.sin(time * 0.5) * 0.04 * (1 - mp);
        const driftY = Math.cos(time * 0.4) * 0.04 + Math.sin(time * 0.7) * 0.02;
        
        leftPos.current.x = lerp(leftPos.current.x, leftTargetX + driftX, 0.025);
        leftPos.current.y = lerp(leftPos.current.y, 0.5 + driftY, 0.025);
        rightPos.current.x = lerp(rightPos.current.x, rightTargetX - driftX, 0.025);
        rightPos.current.y = lerp(rightPos.current.y, 0.5 - driftY * 0.5, 0.025);
        
        const mergedIntensity = mp * 0.2;
        
        // Left glow
        const leftBaseOpacity = leftActive ? pulseOpacity * 1.3 : pulseOpacity * 0.6;
        const leftOpacity = leftBaseOpacity + mergedIntensity;
        const leftRadius = canvas.width * lerp(0.35, 0.5, mp);
        
        const leftGradient = ctx.createRadialGradient(
          leftPos.current.x * canvas.width,
          leftPos.current.y * canvas.height,
          0,
          leftPos.current.x * canvas.width,
          leftPos.current.y * canvas.height,
          leftRadius
        );
        leftGradient.addColorStop(0, `rgba(255, 170, 100, ${leftOpacity})`);
        leftGradient.addColorStop(0.3, `rgba(255, 190, 130, ${leftOpacity * 0.5})`);
        leftGradient.addColorStop(0.6, `rgba(255, 210, 160, ${leftOpacity * 0.2})`);
        leftGradient.addColorStop(1, 'rgba(255, 230, 190, 0)');
        
        ctx.fillStyle = leftGradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Right glow
        const rightBaseOpacity = rightActive ? pulseOpacity * 1.3 : pulseOpacity * 0.6;
        const rightOpacity = rightBaseOpacity + mergedIntensity;
        const rightRadius = canvas.width * lerp(0.35, 0.5, mp);
        
        const rightGradient = ctx.createRadialGradient(
          rightPos.current.x * canvas.width,
          rightPos.current.y * canvas.height,
          0,
          rightPos.current.x * canvas.width,
          rightPos.current.y * canvas.height,
          rightRadius
        );
        rightGradient.addColorStop(0, `rgba(255, 170, 100, ${rightOpacity})`);
        rightGradient.addColorStop(0.3, `rgba(255, 190, 130, ${rightOpacity * 0.5})`);
        rightGradient.addColorStop(0.6, `rgba(255, 210, 160, ${rightOpacity * 0.2})`);
        rightGradient.addColorStop(1, 'rgba(255, 230, 190, 0)');
        
        ctx.fillStyle = rightGradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Center glow when merged
        if (mp > 0.5) {
          const centerOpacity = (mp - 0.5) * 2 * pulseOpacity * 0.5;
          const centerGradient = ctx.createRadialGradient(
            canvas.width * 0.5,
            canvas.height * 0.45,
            0,
            canvas.width * 0.5,
            canvas.height * 0.45,
            canvas.width * 0.4
          );
          centerGradient.addColorStop(0, `rgba(255, 180, 110, ${centerOpacity})`);
          centerGradient.addColorStop(0.5, `rgba(255, 200, 140, ${centerOpacity * 0.4})`);
          centerGradient.addColorStop(1, 'rgba(255, 220, 170, 0)');
          
          ctx.fillStyle = centerGradient;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      } else {
        // ========== CURSOR-FOLLOWING GLOW ==========
        if (isMobile.current) {
          const driftX = 0.5 + Math.sin(time * 0.25) * 0.15 + Math.sin(time * 0.6) * 0.08;
          const driftY = 0.4 + Math.cos(time * 0.35) * 0.1 + Math.cos(time * 0.5) * 0.06;
          currentPos.current.x = driftX;
          currentPos.current.y = driftY;
        } else {
          currentPos.current.x = lerp(currentPos.current.x, mousePos.current.x, 0.035);
          currentPos.current.y = lerp(currentPos.current.y, mousePos.current.y, 0.035);
        }
        
        // BIGGER cursor glow - 65% of viewport
        const baseRadius = Math.min(canvas.width, canvas.height) * 0.65;
        const pulsingRadius = baseRadius + Math.sin(time * 0.6) * (baseRadius * 0.05);
        
        const gradient = ctx.createRadialGradient(
          currentPos.current.x * canvas.width,
          currentPos.current.y * canvas.height,
          0,
          currentPos.current.x * canvas.width,
          currentPos.current.y * canvas.height,
          pulsingRadius
        );
        
        // Peachy gradient with 0.25 base
        gradient.addColorStop(0, `rgba(255, 165, 100, ${pulseOpacity * 0.7})`);
        gradient.addColorStop(0.25, `rgba(255, 185, 125, ${pulseOpacity * 0.5})`);
        gradient.addColorStop(0.5, `rgba(255, 200, 150, ${pulseOpacity * 0.3})`);
        gradient.addColorStop(0.75, `rgba(255, 215, 175, ${pulseOpacity * 0.12})`);
        gradient.addColorStop(1, 'rgba(255, 230, 195, 0)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // ========== DROP GLOW (goes DOWN - text disappearing) ==========
        if (dropGlowActive.current) {
          // Move down toward middle
          dropGlowY.current = lerp(dropGlowY.current, 0.45, 0.045);
          
          // Fade out as it reaches middle
          if (dropGlowY.current > 0.25) {
            dropGlowOpacity.current = lerp(dropGlowOpacity.current, 0, 0.06);
          }
          
          if (dropGlowOpacity.current < 0.01) {
            dropGlowActive.current = false;
          }
          
          if (dropGlowOpacity.current > 0.01) {
            const dropRadius = Math.min(canvas.width, canvas.height) * 0.45;
            const dropGradient = ctx.createRadialGradient(
              canvas.width * 0.5,
              dropGlowY.current * canvas.height,
              0,
              canvas.width * 0.5,
              dropGlowY.current * canvas.height,
              dropRadius
            );
            
            dropGradient.addColorStop(0, `rgba(255, 155, 85, ${dropGlowOpacity.current})`);
            dropGradient.addColorStop(0.35, `rgba(255, 180, 115, ${dropGlowOpacity.current * 0.5})`);
            dropGradient.addColorStop(0.7, `rgba(255, 205, 150, ${dropGlowOpacity.current * 0.2})`);
            dropGradient.addColorStop(1, 'rgba(255, 225, 180, 0)');
            
            ctx.fillStyle = dropGradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
        }
        
        // ========== RISE GLOW (goes UP - text appearing) ==========
        if (riseGlowActive.current) {
          // Move up toward top - SLOWER so it lingers ~1s longer
          riseGlowY.current = lerp(riseGlowY.current, -0.1, 0.018);
          
          // Fade out as it rises - delayed threshold and slower fade
          if (riseGlowY.current < 0.2) {
            riseGlowOpacity.current = lerp(riseGlowOpacity.current, 0, 0.025);
          }
          
          if (riseGlowOpacity.current < 0.01) {
            riseGlowActive.current = false;
          }
          
          if (riseGlowOpacity.current > 0.01) {
            const riseRadius = Math.min(canvas.width, canvas.height) * 0.45;
            const riseGradient = ctx.createRadialGradient(
              canvas.width * 0.5,
              riseGlowY.current * canvas.height,
              0,
              canvas.width * 0.5,
              riseGlowY.current * canvas.height,
              riseRadius
            );
            
            riseGradient.addColorStop(0, `rgba(255, 155, 85, ${riseGlowOpacity.current})`);
            riseGradient.addColorStop(0.35, `rgba(255, 180, 115, ${riseGlowOpacity.current * 0.5})`);
            riseGradient.addColorStop(0.7, `rgba(255, 205, 150, ${riseGlowOpacity.current * 0.2})`);
            riseGradient.addColorStop(1, 'rgba(255, 225, 180, 0)');
            
            ctx.fillStyle = riseGradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
        }
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [splitMode, leftActive, rightActive, merged]);

  return (
    <div className="glow-container">
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: '100%' }}
        data-testid="glow-background"
      />
    </div>
  );
};

export default GlowBackground;
